import { Alert } from '@unmind/design-system';
import RoutePath from 'App/RoutePath';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { getChannel } from 'utils';
import {
  LoggedOutScreenSubtitle,
  LoggedOutScreenTitle,
  LoggedOutScreenWrapper,
} from 'LoggedOut/LoggedOutScreenWrapper';
import { StyledSubmitButton } from '../CommonFormStyledComponents';
import { SignUpWithSSOButton } from '../../SignUpWithSSOButton';
import { EmailConfirmationVariant } from '../../SignUpEmailConfirmation';
import {
  DisplayEmail,
  useVerifyUserForRegistrationByMembershipId,
} from './hooks/useVerifyUserForRegistrationByMembershipId';
import { TrackedVerificationStep } from './constants';
import { VerificationTracker } from './types';

import './MembershipIdVerification.scss';

const STEP_NAME = TrackedVerificationStep.ConfirmIdentity;

export interface MembershipIdVerificationProps {
  tracker: VerificationTracker;
  membershipId: string;
  organisationName?: string | null;
  showSSOOption?: boolean;
  onInvalidLink(): void;
}

export const MembershipIdVerification = ({
  tracker,
  membershipId,
  organisationName,
  showSSOOption = false,
  onInvalidLink,
}: MembershipIdVerificationProps) => {
  const { t: translate } = useTranslation('logged_out', {
    keyPrefix: 'sign_up.membership_id_verification',
  });
  const { t: translateShared } = useTranslation('shared');
  const history = useHistory();
  const [error, setError] = useState<string>();

  useEffect(() => {
    tracker.stepViewed(STEP_NAME);
  }, [tracker]);

  const goToConfirmation = (
    variant: EmailConfirmationVariant,
    { value, masked }: DisplayEmail,
  ) => {
    history.push({
      pathname: RoutePath.EmailConfirmation,
      state: { email: value, emailIsMasked: masked, variant },
    });
  };

  const { verifyByMembershipId, loading } =
    useVerifyUserForRegistrationByMembershipId({
      tracker,
      onVerificationEmailSent: displayEmail =>
        goToConfirmation(EmailConfirmationVariant.SignUpLink, displayEmail),
      onSignInLinkSent: displayEmail =>
        goToConfirmation(EmailConfirmationVariant.SignInLink, displayEmail),
      onInvalidLink,
      onError: setError,
    });

  const emailLinkLabel = translate('email_link_button.label');

  return (
    <LoggedOutScreenWrapper dataTestId="membership-id-verification">
      <LoggedOutScreenTitle>
        {translate('heading', { brandName: getChannel().brandName })}
      </LoggedOutScreenTitle>
      <LoggedOutScreenSubtitle>
        {translate(showSSOOption ? 'subtitle_with_sso' : 'subtitle', {
          organisationName,
        })}
      </LoggedOutScreenSubtitle>
      <div className="membership-id-verification-actions">
        {showSSOOption && (
          <>
            <SignUpWithSSOButton
              variant="primary"
              onClick={() => setError(undefined)}
              onError={() =>
                setError(translateShared('errors.messages.failed'))
              }
            />
            <div className="membership-id-verification-divider">
              <span className="body-xs divider-label">
                {translate('divider.or')}
              </span>
            </div>
          </>
        )}
        <StyledSubmitButton
          variant={showSSOOption ? 'secondary' : 'primary'}
          loading={loading}
          disabled={loading}
          label={emailLinkLabel}
          aria-label={emailLinkLabel}
          onClick={async () => {
            setError(undefined);
            tracker.stepCompleted(STEP_NAME);

            await verifyByMembershipId(membershipId);
          }}
        />
      </div>

      {Boolean(error) && (
        <div className="membership-id-verification-error">
          <Alert variant="error">
            <p>{error}</p>
          </Alert>
        </div>
      )}
    </LoggedOutScreenWrapper>
  );
};
